/**
 * ClienteMeusPedidos — Lista paginada de todos os romaneios do cliente.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FileText, ChevronRight, Clock, CheckCircle, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; } };

const STATUS_INFO = {
  pago: { label: 'Pago', icon: CheckCircle, cls: 'text-green-700 bg-green-100 dark:bg-green-900/30' },
  pendente: { label: 'Pendente', icon: Clock, cls: 'text-amber-700 bg-amber-100 dark:bg-amber-900/30' },
  parcial: { label: 'Parcial', icon: Clock, cls: 'text-blue-700 bg-blue-100 dark:bg-blue-900/30' },
  atrasado: { label: 'Atrasado', icon: AlertTriangle, cls: 'text-red-700 bg-red-100 dark:bg-red-900/30' },
};

export default function ClienteMeusPedidos({ onlyAtrasados = false }) {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/cliente/me/romaneios`)
      .then(r => setList(r.data || []))
      .catch(() => toast.error('Erro ao carregar pedidos'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = onlyAtrasados
    ? list.filter(r => (r.status_pagamento || '').toLowerCase() === 'atrasado' || r.is_atrasado)
    : list;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
          {onlyAtrasados ? (
            <><AlertTriangle className="w-6 h-6 text-red-500" /> Pedidos Atrasados</>
          ) : (
            <><FileText className="w-6 h-6 text-blue-600" /> Meus Pedidos</>
          )}
        </h1>
        <p className="text-sm text-gray-500">
          {onlyAtrasados ? 'Pedidos com pagamento em atraso.' : 'Histórico completo dos seus pedidos.'}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" data-testid="empty-pedidos">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">
            {onlyAtrasados ? 'Nenhum pedido atrasado. 🎉' : 'Você ainda não tem pedidos.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(r => {
            const st = STATUS_INFO[(r.status_pagamento || 'pendente').toLowerCase()] || STATUS_INFO.pendente;
            const Icon = st.icon;
            return (
              <button
                key={r.id}
                onClick={() => navigate(`/cliente/romaneio/${r.id}`)}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:shadow-md transition-all text-left flex items-center gap-3"
                data-testid={`pedido-card-${r.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${st.cls}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      Pedido #{r.numero_pedido}
                    </p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {fmtDate(r.data_pedido || r.created_at)} · {fmtBRL(r.valor_total)}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
